// @flow
import React, { Component } from 'react'
import { Redirect, withRouter } from 'react-router-dom'
import moment from 'moment'
import { message as antdMessage } from 'antd'
import { connect } from 'react-redux'
import { bindActionCreators } from 'redux'

import Path from 'routes/path'
import { setUIState, containerIds } from 'redux/modules/ui'
import {
  actions as ReservationDetailActions,
  type State as ReservationDetailState,
} from 'redux/modules/reservationDetail'
import {
  actions as ReservationEditActions,
  type State as ReservationEditState,
} from 'redux/modules/reservationEdit'
import { showErrorMessage } from 'helpers/error'

import MainTemplate from 'components/templates/MainTemplate'
import ReservationEditPage from 'components/pages/ReservationEditPage'
import ReservationEditForm from 'components/organisms/ReservationEditForm'
import { getReservationEditFormProps } from 'components/organisms/ReservationEditForm/presenter'
import LoadingView from 'components/atoms/LoadingView'
import ReservationSidebarContainer from 'containers/organisms/ReservationSidebar'

import graphql from './graphql'
import { accountLoginUrl } from 'frontend_core/dist/helpers/url'
import PaidyCharge from 'containers/organisms/PaidyCharge'

export type PropTypes = {
  match: {
    params: {
      ownerName: string,
    },
  },
  location: {
    pathname: string,
  },
  history: {
    replace: Function,
    push: Function,
  },
  ui: {
    isLoading?: ?boolean,
    isModalActive?: ?boolean,
    isPaidyChargeActive?: ?boolean,
  },
  actions: {
    setUIState: Function,
    fetchReservationDetail: Function,
    editReservationDryrun: Function,
    editReservation: Function,
  },
  reservationDetail: ReservationDetailState,
  reservationEdit: ReservationEditState,
}

class ReservationEditPageContainer extends Component<PropTypes, *, *> {
  static async getInitialProps({ req, res, params, config, isLogin }) {
    if (!isLogin)
      return { redirect: { to: accountLoginUrl(config), status: 302 } }
    return
  }

  constructor(props: PropTypes) {
    super(props)
    this.onClickSendConfirm = this.onClickSendConfirm.bind(this)
    this.onClickSend = this.onClickSend.bind(this)
    this.closeModal = this.closeModal.bind(this)
    this.onClickBackToMessage = this.onClickBackToMessage.bind(this)
    this.onPaidyChargeSuccess = this.onPaidyChargeSuccess.bind(this)
    this.onPaidyChargeFailed = this.onPaidyChargeFailed.bind(this)
    this.onPaidyChargeClose = this.onPaidyChargeClose.bind(this)
  }

  componentDidMount() {
    const { actions, reservationDetail, match, config, data } = this.props
    const { detail, messages } = reservationDetail

    // 直リンの場合の初期データ取得
    if (!detail.id) {
      actions.fetchReservationDetail({
        userId: data.viewer.id,
        reservationId: match.params.reservationId,
      })
    }

    actions.setUIState(containerIds.RESERVATION_DETAIL, {
      isPaidyChargeActive: false,
    })

    // モーダル閉じる
    this.closeModal()
    window.scrollTo(0, 0)
  }

  componentDidUpdate(prevProps: PropTypes) {
    const {
      actions,
      data,
      reservationDetail,
      reservationEdit,
      history,
      match,
    } = this.props
    const {
      reservationDetail: prevReservationDetail,
      reservationEdit: prevReservationEdit,
    } = prevProps

    if (reservationDetail.detail.id !== prevReservationDetail.detail.id) {
      actions.editReservationDryrun({
        userId: parseInt(data.viewer.id, 10),
      })
    }

    if (
      reservationEdit.successEditReservation &&
      !prevReservationEdit.successEditReservation
    ) {
      antdMessage.success(i18n.t('page.shared.sendSuccess'))
      history.push(location.pathname)
      history.replace(Path.reservationDetail(match.params.reservationId))
    }

    // エラー系の表示
    if (
      reservationEdit.failedEditReservation !== null &&
      prevReservationEdit.failedEditReservation === null
    ) {
      showErrorMessage(reservationEdit.failedEditReservation)
    }
  }

  onClickBackToMessage: Function
  onClickBackToMessage(e: Event) {
    const { history, location, match } = this.props
    e.preventDefault()
    history.push(location.pathname)
    history.replace(Path.reservationDetail(match.params.reservationId))
  }

  onClickSendConfirm: Function
  onClickSendConfirm(e: Event) {
    const { actions } = this.props
    e.preventDefault()

    actions.setUIState(containerIds.RESERVATION_DETAIL, {
      isModalActive: true,
    })
  }

  onClickSend: Function
  onClickSend() {
    const { actions, data, reservationEdit } = this.props
    const unapprovedReservation = (reservationEdit || {}).unapprovedReservation
    const isNeedAdditionalPaidyChargeForChange = (
      (unapprovedReservation || {}).billing || {}
    ).is_need_additional_paidy_charge_for_change

    if (isNeedAdditionalPaidyChargeForChange) {
      actions.setUIState(containerIds.RESERVATION_DETAIL, {
        isPaidyChargeActive: true,
      })
    } else {
      actions.editReservation({
        userId: data.viewer.id,
      })
    }
  }

  onPaidyChargeSuccess: Function
  onPaidyChargeSuccess(paidyPaymentId, paidyChargeAmount) {
    const { actions, data } = this.props
    // 2重で呼ばれないようにここでフラグを落とす
    actions.setUIState(containerIds.RESERVATION_DETAIL, {
      isModalActive: false,
      isPaidyChargeActive: false,
    })
    actions.editReservation({
      userId: data.viewer.id,
      paidyPaymentId: paidyPaymentId,
      paidyChargeAmount: paidyChargeAmount,
    })
  }

  onPaidyChargeFailed: Function
  onPaidyChargeFailed() {
    const { actions, data, match } = this.props
    // 2重で呼ばれないようにここでフラグを落とす
    actions.setUIState(containerIds.RESERVATION_DETAIL, {
      isPaidyChargeActive: false,
      isModalActive: false,
    })
    // 失敗時にメッセージを飛ばす
    actions.sendMessage({
      userId: data.viewer.id,
      reservationId: match.params.reservationId,
      text: i18n.t('page.dashboard.reservation.error.failedPaidyCharge'),
      isAuto: true,
    })
  }

  onPaidyChargeClose: Function
  onPaidyChargeClose() {
    const { actions } = this.props
    actions.setUIState(containerIds.RESERVATION_DETAIL, {
      isModalActive: false,
      isPaidyChargeActive: false,
    })
  }

  closeModal: Function
  closeModal() {
    const { actions } = this.props
    actions.setUIState(containerIds.RESERVATION_DETAIL, {
      isModalActive: false,
    })
  }

  render() {
    const {
      location,
      history,
      match,
      reservationDetail,
      reservationEdit,
      ui,
      data,
      actions,
    } = this.props
    const { detail } = reservationDetail

    // 予約編集可フラグが立っていないで直リンクした場合はメッセージ画面に戻す
    if (detail.id && !detail.can_be_edited_detail) {
      antdMessage.warning(i18n.t('page.dashboard.reservation.error.cannotEdit'))
      return (
        <Redirect to={Path.reservationDetail(match.params.reservationId)} />
      )
    }

    return (
      <MainTemplate
        title={i18n.t('server.www.reservationEdit.title', {
          id: detail.id || '',
        })}
        isFixedHeader
        hideMustHeader
        hasFooter={false}
        {...this.props}
      >
        <ReservationEditPage
          sidebarComponent={<ReservationSidebarContainer />}
          isShowContentsSp
          closeModal={this.closeModal}
          isModalActive={!!ui.isModalActive}
          onClickPositiveButton={this.onClickSend}
          isLoading={ui.isLoading}
          reservationEdit={reservationEdit}
        >
          <ReservationEditForm
            {...getReservationEditFormProps(
              data.viewer.id,
              reservationDetail,
              reservationEdit,
              actions,
              this.onClickBackToMessage,
              this.onClickSendConfirm,
            )}
          />
        </ReservationEditPage>
        {ui &&
          ui.isPaidyChargeActive && (
            <PaidyCharge
              roomId={reservationEdit.unapprovedReservation.room.id}
              rentType={reservationEdit.unapprovedReservation.rent_type}
              billing={reservationEdit.unapprovedReservation.billing}
              onPaidyChargeSuccess={this.onPaidyChargeSuccess}
              onPaidyChargeFailed={this.onPaidyChargeFailed}
              onPaidyChargeClose={this.onPaidyChargeClose}
            />
          )}
      </MainTemplate>
    )
  }
}

function mapStateToProps(state) {
  return {
    ui: state.ui,
    reservationDetail: state.reservationDetail,
    reservationEdit: state.reservationEdit,
    config: state.config,
  }
}
function mapDispatchToProps(dispatch: Dispatch<*>) {
  const bindActions = {
    setUIState,
    ...ReservationDetailActions,
    ...ReservationEditActions,
  }
  return { actions: bindActionCreators(bindActions, dispatch) }
}

const WrappedContainer = withRouter(
  connect(
    mapStateToProps,
    mapDispatchToProps,
  )(graphql(ReservationEditPageContainer)),
)

export default WrappedContainer



// WEBPACK FOOTER //
// ./src/containers/pages/ReservationEditPage/index.jsx